import React, { useEffect, useState } from 'react';
import { Button, LinkText, Table } from '@adc/parallax-component-library';
import DynamicTable, { CheckboxFilterSection } from '../../table/DynamicTable';
import CreateSiteModal from '../../modal/CreateSiteModal';
import { Site } from '../../../models';
import { EntityStatus } from 'adc-sirius-module-common';
import i18n from '../../../utilities/i18n';
import { enumToCheckboxOptions } from '../../../utilities/helpers';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  createSiteMutateOptions,
  sitesQueryOptions,
} from '../../../queryOptions';

type SitesTableProps = {
  isLoading: boolean;
  hasLoadingError: boolean;
  sites: Site[];
  studyKey: string;
  navigateToSite: (siteKey: string, siteLabel: string) => void;
  canCreateSite: boolean;
};

export const SitesTable = ({
  isLoading,
  hasLoadingError,
  sites,
  studyKey,
  navigateToSite,
  canCreateSite = false,
}: SitesTableProps) => {
  const queryClient = useQueryClient();

  const [filteredSites, setFilteredSites] = useState<Site[]>([]);
  const [siteStatusFilters, setSiteStatusFilters] = useState<
    CheckboxFilterSection[]
  >([
    {
      id: 'status',
      label: i18n.t('Entity.status'),
      items: enumToCheckboxOptions(EntityStatus, 'Status'),
    },
  ]);

  const createSite = useMutation(
    createSiteMutateOptions({
      onSuccess: (site) => {
        queryClient.setQueryData(
          sitesQueryOptions({ studyKey }).queryKey,
          (sites: Site[] | undefined) => [...(sites ?? []), site],
        );
      },
    }),
  );

  useEffect(() => {
    let nextFilteredSites = sites;

    siteStatusFilters.forEach((filter) => {
      const activeFilterValues = filter.items
        .filter((item) => item.value)
        .map((item) => item.id);

      if (activeFilterValues.length > 0 && filter.id === 'status') {
        nextFilteredSites = nextFilteredSites.filter((site) =>
          activeFilterValues.includes(String(site.status)),
        );
      }
    });

    setFilteredSites(nextFilteredSites);
  }, [siteStatusFilters, sites]);

  const getStatusLabel = (status: EntityStatus) => {
    return i18n.t(`Status.${EntityStatus[status].toLowerCase()}`);
  };

  const sitesTableColumnData = [
    { label: i18n.t('Entity.name'), dataKey: 'name' },
    { label: i18n.t('Entity.region'), dataKey: 'region' },
    {
      label: i18n.t('Entity.status'),
      dataKey: 'status',
      getSortValue: (site: Site) => getStatusLabel(site.status),
    },
  ];

  const sitesTableCellRenderer = (dataKey: string, site: Site) => {
    if (dataKey === 'name') {
      return (
        <Table.TextCell key={dataKey}>
          <LinkText
            onPress={() => navigateToSite(site.key, site.name)}
            type="$bodySmall.default"
          >
            {site[dataKey]}
          </LinkText>
        </Table.TextCell>
      );
    } else if (dataKey === 'status') {
      return (
        <Table.TextCell key={dataKey}>
          {getStatusLabel(site.status)}
        </Table.TextCell>
      );
    } else {
      return <Table.TextCell key={dataKey}>{site[dataKey]}</Table.TextCell>;
    }
  };

  return (
    <DynamicTable
      isLoading={isLoading}
      hasLoadingError={hasLoadingError}
      title={i18n.t('Page_Study.table_sites_title')}
      description={i18n.t('Page_Study.table_sites_description')}
      columnData={sitesTableColumnData}
      rowData={filteredSites}
      cellRenderer={sitesTableCellRenderer}
      enableFiltering={true}
      checkboxFilters={siteStatusFilters}
      onFiltersChange={setSiteStatusFilters}
      onFiltersReset={() => {
        setSiteStatusFilters((prevFilters) =>
          prevFilters.map((filter) => ({
            ...filter,
            items: filter.items.map((item) => ({
              ...item,
              value: false,
            })),
          })),
        );
      }}
      toolbarRightContent={
        canCreateSite ? (
          <CreateSiteModal
            studyKey={studyKey}
            trigger={
              <Button
                size="medium"
                label={i18n.t('Page_Study.add_site')}
                onPress={() => {}}
                disabled={createSite.isPending}
                testID="create-site-button"
              />
            }
            onSuccess={async (newSite: Site) => {
              await createSite.mutateAsync({ site: newSite });
            }}
          />
        ) : null
      }
      testID="sites-table"
    />
  );
};
